import React from 'react';
import { User } from 'lucide-react';
import ContactManager from '../Common/ContactManager';
import FormModalLayout from '../Common/FormModalLayout';
import Can from '../Common/Can';
import { P } from '../../constants/permissions';

const PersonModal = ({ person, onSubmit, onClose, resolveContacts }) => {
  const [formData, setFormData] = React.useState({
    name: person?.name || '',
    cpf: person?.cpf || '',
    status: person?.status || 'ACTIVE',
    address: person?.address || '',
    city: person?.city || '',
    state: person?.state || '',
    zip_code: person?.zip_code || '',
    email: person?.email || '',
  });

  const [contacts, setContacts] = React.useState(person?.contacts || []);

  React.useEffect(() => {
    if (person) {
      setFormData({
        name: person.name || '',
        cpf: person.cpf || '',
        status: person.is_active === false ? 'INACTIVE' : 'ACTIVE',
        address: person.address || '',
        city: person.city || '',
        state: person.state || '',
        zip_code: person.zip_code || '',
        email: person.email || '',
      });

      if (resolveContacts) {
        resolveContacts(person)
          .then((loaded) => {
            setContacts(loaded);
          })
          .catch(() => {
            setContacts([]);
          });
      } else {
        setContacts([]);
      }
    } else {
      setFormData({
        name: '',
        cpf: '',
        status: 'ACTIVE',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        email: '',
      });
      setContacts([]);
    }
  }, [person]); // resolveContacts muda a cada render e apagava o formulário

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      contacts,
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const newData = { ...prev, [name]: value };

      if (name === 'type') {
        if (value === 'INDIVIDUAL') {
          newData.cnpj = '';
        } else {
          newData.cpf = '';
        }
      }

      return newData;
    });
  };

  return (
    <FormModalLayout
      title={person ? 'Editar pessoa' : 'Nova pessoa'}
      subtitle="Cadastro de pessoa física com endereço e contatos."
      headerIcon={User}
      onClose={onClose}
      size="4xl"
      historyEntityType="persons"
      historyEntityId={person?.id}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-outline">
            Cancelar
          </button>
          <Can permission={P.IDENTITY_ACCESS}>
            <button type="submit" form="person-modal-form" className="btn btn-primary">
              {person ? 'Atualizar' : 'Criar'}
            </button>
          </Can>
        </>
      }
    >
      <form id="person-modal-form" onSubmit={handleSubmit} className="space-y-8">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-10">
          <div className="space-y-5">
            <h3 className="form-section-title">Informações básicas</h3>

            <div className="form-group">
              <label htmlFor="pe-name" className="form-label form-label-required">
                Nome
              </label>
              <input
                id="pe-name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="pe-cpf" className="form-label form-label-required">
                CPF
              </label>
              <input
                id="pe-cpf"
                type="text"
                name="cpf"
                value={formData.cpf}
                onChange={handleChange}
                className="form-input"
                required
                placeholder="000.000.000-00"
              />
            </div>

            <div className="form-group">
              <label htmlFor="pe-email" className={`form-label ${person ? '' : 'form-label-required'}`}>
                E-mail
              </label>
              <input
                id="pe-email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-input"
                required={!person}
                placeholder="usuario@provedor.com"
                autoComplete="off"
              />
              {!person && (
                <p className="mt-1 text-xs text-gray-500">
                  O acesso ao portal é criado automaticamente. A senha provisória e o link de ativação vão para este e-mail.
                </p>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="pe-status" className="form-label">
                Status
              </label>
              <select
                id="pe-status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="form-select"
              >
                <option value="ACTIVE">Ativo</option>
                <option value="INACTIVE">Inativo</option>
              </select>
            </div>
          </div>

          <div className="space-y-5">
            <h3 className="form-section-title">Endereço</h3>

            <div className="form-group">
              <label htmlFor="pe-addr" className="form-label">
                Endereço
              </label>
              <input
                id="pe-addr"
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="form-input"
                placeholder="Rua, número, bairro"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <label htmlFor="pe-city" className="form-label">
                  Cidade
                </label>
                <input
                  id="pe-city"
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="pe-state" className="form-label">
                  UF
                </label>
                <input
                  id="pe-state"
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="form-input"
                  maxLength="2"
                  placeholder="SP"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="pe-cep" className="form-label">
                CEP
              </label>
              <input
                id="pe-cep"
                type="text"
                name="zip_code"
                value={formData.zip_code}
                onChange={handleChange}
                className="form-input"
                placeholder="00000-000"
              />
            </div>
          </div>
        </div>

        <div>
          <h3 className="form-section-title mb-4">Contatos</h3>
          <ContactManager contacts={contacts} onChange={setContacts} />
        </div>
      </form>
    </FormModalLayout>
  );
};

export default PersonModal;
